<form
  *ngrxLet="patient$ as patient"
  class="flex flex-col gap-4"
  [formGroup]="form"
>
  <div class="flex flex-row flex-wrap gap-4">
    <pr-patient-details-compare-field
      *ngrxLet="
        'firstName' | map: getPatientNameFieldState$ : this | async as state
      "
      class="flex grow"
      [state]="state"
      (revertChange)="service.revertNameChange('firstName', state)"
    >
      <mat-form-field class="flex-1">
        <mat-label>First Name</mat-label>
        <input matInput type="text" formControlName="firstName" required />
      </mat-form-field>
    </pr-patient-details-compare-field>

    <pr-patient-details-compare-field
      *ngrxLet="
        'lastName' | map: getPatientNameFieldState$ : this | async as state
      "
      class="flex grow"
      [state]="state"
      (revertChange)="service.revertNameChange('lastName', state)"
    >
      <mat-form-field class="flex-1">
        <mat-label>Last Name</mat-label>
        <input matInput type="text" formControlName="lastName" required />
      </mat-form-field>
    </pr-patient-details-compare-field>
  </div>

  <pr-patient-details-compare-field
    *ngrxLet="'gender' | map: getBasePatientFieldState$ : this | async as state"
    [state]="state"
    (revertChange)="service.revertChange('gender', state)"
  >
    <mat-form-field class="flex-1">
      <mat-label>Gender</mat-label>
      <mat-select formControlName="gender" required>
        <mat-option
          *ngFor="let gender of genders; trackBy: trackByGender"
          [value]="gender"
        >
          {{ gender | splitCamel | titlecase }}
        </mat-option>
      </mat-select>
    </mat-form-field>
  </pr-patient-details-compare-field>

  <pr-patient-details-compare-field
    *ngrxLet="
      'dateOfBirth' | map: getBasePatientFieldState$ : this | async as state
    "
    [state]="state"
    (revertChange)="service.revertChange('dateOfBirth', state)"
  >
    <pt-iso-date-selector
      class="flex-1"
      placeholder="Date of birth"
      formControlName="dateOfBirth"
      [required]="'dateOfBirth' | map: isRequired$ : this | async"
      [max]="today"
    />
  </pr-patient-details-compare-field>

  <pr-patient-details-compare-field
    *ngrxLet="
      ['address', 'address']
        | map: getBasePatientNestedFieldState$ : this
        | async as state
    "
    [state]="state"
    (revertChange)="service.revertAddressChange(state)"
  >
    <pr-address-input class="flex-1" formControlName="address" />
  </pr-patient-details-compare-field>

  <pr-patient-details-compare-field
    *ngrxLet="'email' | map: getBasePatientFieldState$ : this | async as state"
    [state]="state"
    (revertChange)="service.revertChange('email', state)"
  >
    <mat-form-field class="flex-1">
      <mat-label>Email</mat-label>
      <input
        matInput
        type="email"
        formControlName="email"
        [required]="'email' | map: isRequired$ : this | async"
      />
    </mat-form-field>
  </pr-patient-details-compare-field>

  <div formArrayName="contactNumbers" class="flex flex-col gap-3">
    @for (control of contactNumberControls.controls; track $index) {
      <pr-patient-details-compare-field
        *ngrxLet="
          [control, $index]
            | map: getContactNumberFieldStateByIndex$ : this
            | async as state
        "
        [state]="state"
        (revertChange)="
          service.revertContactNumberChange(control, state, $index)
        "
      >
        <pr-contact-number-input
          class="flex-1"
          [form]="control"
          [isFirst]="$first"
          [index]="$index"
          [required]="'contactNumbers' | map: isRequired$ : this | async"
        >
          <div class="flex flex-row content-center">
            <button
              mat-icon-button
              [disabled]="service.disabled$ | async"
              (click)="contactNumberControls.addControl($index)"
            >
              <mat-icon>add</mat-icon>
            </button>
            <button
              mat-icon-button
              [disabled]="service.disabled$ | async"
              (click)="contactNumberControls.removeControl($index)"
            >
              <mat-icon>remove</mat-icon>
            </button>
          </div>
        </pr-contact-number-input>
      </pr-patient-details-compare-field>
    }
  </div>

  <ng-container *prIsRegion="[Region.NewZealand]">
    @if (showSchoolField$ | async) {
      <pr-patient-details-compare-field
        *ngrxLet="getSchoolFieldState$() | async as state"
        [state]="state"
        (revertChange)="service.revertSchoolChange(state)"
      >
        <mat-form-field class="flex-1">
          <mat-label>School Attended</mat-label>
          <input
            placeholder="Search schools..."
            formControlName="school"
            matInput
            [matAutocomplete]="schoolAuto"
          />
          <mat-hint
            >School Attended is used for Ministry of Health (MOH)
            patients.</mat-hint
          >
          <mat-autocomplete
            #schoolAuto="matAutocomplete"
            [displayWith]="schoolDisplayFn"
          >
            @for (
              school of schoolSearch.results$ | async;
              track school.schoolNumber
            ) {
              <mat-option [value]="school">{{ school.schoolName }}</mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </pr-patient-details-compare-field>
    }
  </ng-container>

  @if (service.getReferrerFieldState$() | async; as referrerState) {
    <pr-patient-details-compare-field
      [state]="referrerState"
      (revertChange)="service.revertReferrerChange(referrerState)"
    >
      <mat-form-field class="flex-1">
        <mat-label>Referrer</mat-label>
        <mat-select
          formControlName="referrer"
          [compareWith]="isSameReferrer"
          [required]="'referrer' | map: isRequired$ : this | async"
        >
          @for (referrer of referrers$ | async; track referrer.name) {
            <mat-option [value]="referrer">
              {{ referrer.name }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </pr-patient-details-compare-field>
  }

  @if (hasHealthCards$ | async) {
    <span>Health Fund Card</span>

    @if (healthCardEnabled$('healthFund') | async) {
      <pr-patient-details-compare-field
        *ngrxLet="
          healthFundCard
            | pairMap$: 'healthFundCard' : getHealthCardFieldState$ : this
            | async as state
        "
        [state]="state"
        (revertChange)="
          service.revertHealthCardChange(
            healthFundCard,
            state,
            'healthFundCard'
          )
        "
      >
        <pr-patient-details-health-fund-form
          class="flex-1"
          [form]="form.controls.healthFundCard"
        />
      </pr-patient-details-compare-field>
    }

    @if (healthCardEnabled$('medicare') | async) {
      <span>Medicare Card</span>
      <pr-patient-details-compare-field
        *ngrxLet="
          medicareCard
            | pairMap$: 'medicareCard' : getHealthCardFieldState$ : this
            | async as state
        "
        [state]="state"
        (revertChange)="
          service.revertHealthCardChange(medicareCard, state, 'medicareCard')
        "
      >
        <pr-patient-details-medicare-form
          class="flex-1"
          [disabled]="service.disabled$ | async"
          [form]="form.controls.medicareCard"
        />
      </pr-patient-details-compare-field>
    }

    @if (healthCardEnabled$('dva') | async) {
      <span>DVA Card</span>
      <pr-patient-details-compare-field
        *ngrxLet="
          dvaCard
            | pairMap$: 'dvaCard' : getHealthCardFieldState$ : this
            | async as state
        "
        [state]="state"
        (revertChange)="
          service.revertHealthCardChange(dvaCard, state, 'dvaCard')
        "
      >
        <pr-patient-details-dva-form
          class="flex-1"
          [form]="form.controls.dvaCard"
        />
      </pr-patient-details-compare-field>
    }

    @if (healthCardEnabled$('nhi') | async) {
      <span>NHI Number</span>
      <pr-patient-details-compare-field
        *ngrxLet="
          'nationalHealthIndexNumber'
            | map: getBasePatientFieldState$ : this
            | async as state
        "
        [state]="state"
        (revertChange)="
          service.revertChange('nationalHealthIndexNumber', state)
        "
      >
        <pr-national-health-index-number-form
          class="flex-1"
          [disabled]="service.disabled$ | async"
          [nationalHealthIndexNumber]="
            form.controls.nationalHealthIndexNumber.value
          "
          (nationalHealthIndexNumberInput)="
            nationalHealthIndexNumberTyped($event)
          "
        />
      </pr-patient-details-compare-field>
    }
  }
</form>
